import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Star } from 'lucide-react';
import { mockData } from '../utils/mock';
import Countdown from "../components/Countdown";
import { useTranslation } from "react-i18next";

const Home = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-4 sm:px-6 lg:px-8 min-h-[80vh] flex flex-col justify-center items-center text-center">
{/* Баннер для мобильных */}
<div 
  className="absolute inset-0 bg-cover bg-center sm:hidden"
  style={{ backgroundImage: 'url("/banner-mobile.png")' }}
>
  <div className="absolute inset-0 bg-black/60"></div>
</div>

{/* Баннер для планшетов и выше */}
<div 
  className="absolute inset-0 bg-cover bg-center hidden sm:block"
  style={{ backgroundImage: 'url("/banner-desktop.png.png")' }}
>
  <div className="absolute inset-0 bg-black/60"></div>
</div>


        {/* Content */}
        <div className="relative z-10 max-w-4xl w-full">
          <Badge className="mb-4 sm:mb-6 bg-yellow-600/90 backdrop-blur-sm text-black px-4 py-2 text-sm sm:text-base font-medium shadow-lg">
            🚀 {t("home.hero.badge")}
          </Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 text-white leading-tight drop-shadow-2xl">
            {t("home.hero.title.line1")}
            <br />
            <span className="text-yellow-400">{t("home.hero.title.highlight")}</span> {t("home.hero.title.line2")}
          </h1>

          {/* Countdown */}
          <div className="mb-2 sm:mb-6 flex justify-center">
            <Countdown className="text-sm sm:text-base md:text-lg lg:text-xl" />
          </div>

          <p className="text-lg sm:text-xl text-gray-100 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed drop-shadow-lg">
            {t("home.hero.description")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              asChild
              size="lg" 
              className="bg-yellow-600 hover:bg-yellow-500 text-black font-semibold px-8 py-4 rounded-full text-lg sm:text-xl transition-all duration-300 hover:scale-105 hover:shadow-xl backdrop-blur-sm"
            >
              <Link to="/register">{t("home.hero.apply")}</Link>
            </Button>
            <Button 
              asChild
              variant="outline" 
              size="lg"
              className="border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black font-semibold px-8 py-4 rounded-full text-lg sm:text-xl transition-all duration-300 hover:scale-105 backdrop-blur-sm bg-white/10"
            >
              <Link to="/about">{t("home.hero.learnMore")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Pilot Show Section */}
<section className="py-20 bg-gray-50">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
    
    {/* Картинка */}
    <div className="w-full">
      <img 
        src="/pilot-show.png" 
        alt="Pilot Show" 
        className="rounded-2xl shadow-lg w-full h-auto object-cover"
      />
    </div>

    {/* Текст */}
    <div className="flex flex-col justify-center text-center md:text-left">
      <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">
        {t('home.pilot.title')}
      </h2>
      <p className="text-lg text-gray-600 mb-6">
        {t('home.pilot.subtitle')}
      </p>
      <p className="text-sm text-gray-500 italic">
        {t('home.pilot.subsubtitle')}
      </p>
    </div>
  </div>
</section>

{/* Venue Section */}
<section className="py-20 bg-gray-50">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center gap-10">
    {/* Текст */}
    <div className="w-full md:w-1/2 text-center md:text-left">
      <h2 className="text-4xl font-bold text-black mb-4">
        {t('home.venue.title')}
      </h2>
      <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
        <span className="text-xl font-semibold text-gray-800">
          {t('home.venue.subtitle')}
        </span>
        <img
          src="/spaces-logo.png"
          alt="SPACES"
          className="h-3 object-contain"
        />
      </div>
      <p className="text-lg text-gray-600 leading-relaxed max-w-xl mx-auto md:mx-0">
        {t('home.venue.subsubtitle')}
      </p>
    </div>

    {/* Картинка */}
    <div className="w-full md:w-1/2 flex justify-center">
      <img
        src="/venue.png"
        alt="IT Startup Arena Venue"
        className="w-full max-w-md rounded-xl shadow-lg object-contain"
      />
    </div>

  </div>
</section>



      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            {mockData.stats.map((stat, index) => (
              <div key={index} className="group">
                <div className="mb-4 flex justify-center">
                  <div className="p-4 bg-yellow-100 rounded-full group-hover:bg-yellow-200 transition-all duration-300">
                    <stat.icon className="h-8 w-8 text-black" />
                  </div>
                </div>
                <div className="text-4xl font-bold text-black mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{t(`home.stats.${stat.labelKey}`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-4xl font-bold text-black mb-4">
              {t("home.features.title")}
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              {t("home.features.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockData.features.map((feature, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-200">
                <CardHeader className="text-center pb-4">
                  <div className="mb-4 flex justify-center">
                    <div className="p-4 bg-yellow-100 rounded-full group-hover:bg-yellow-200 transition-all duration-300">
                      <feature.icon className="h-8 w-8 text-black" />
                    </div>
                  </div>
                  <CardTitle className="text-xl font-bold text-black">{t(`home.features.${feature.titleKey}`)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-center leading-relaxed">
                    {t(`home.features.${feature.descriptionKey}`)}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-4xl font-bold text-black mb-4">
              {t("home.success.title")}
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              {t("home.success.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockData.successStories.map((story, index) => {
              const companyName = t(`home.successStories.${story.companyKey}`);
              const industry = t(`home.successStories.${story.industryKey}`);
              const founder = t(`home.successStories.${story.founderKey}`);
              const testimonial = t(`home.successStories.${story.testimonialKey}`);
              const funding = t(`home.successStories.${story.fundingKey}`);

              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-200">
                  <CardHeader>
                    <div className="flex items-center space-x-4 mb-4">
                      {story.image ? (
                        <img
                          src={story.image}
                          alt={companyName}
                          className="w-16 h-16 sm:w-20 sm:h-20 object-contain rounded-full border"
                        />
                      ) : (
                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-yellow-600 rounded-full flex items-center justify-center text-black font-bold text-xl">
                          {companyName[0]}
                        </div>
                      )}
                      <div>
                        <CardTitle className="text-lg sm:text-xl font-bold text-black">{companyName}</CardTitle>
                        <p className="text-sm sm:text-base text-gray-500">{industry}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 italic leading-relaxed mb-4">
                      "{testimonial}"
                    </CardDescription>
                    <div className="flex justify-between items-center">
                      <p className="text-sm sm:text-base font-medium text-black">{founder}</p>
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        {funding}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

{/* Partners Section */}
<section className="py-16 bg-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <h2 className="text-2xl sm:text-3xl font-bold text-center text-black mb-10">
      {t('home.partners')}
    </h2>

    <div className="relative overflow-hidden w-full h-24">
  {/* Лента №1 */}
  <img
    src="/partners.png"
    alt="Partners logos"
    className="absolute top-0 h-24 object-contain animate-strip"
    style={{ left: "0%" }} // обычный старт
  />

  {/* Лента №2 */}
  <img
    src="/partners.png"
    alt="Partners logos"
    className="absolute top-0 h-24 object-contain animate-strip1"
    style={{ left: "50%" }} // начинает дальше вправо
  />

  {/* Лента №3 */}
  <img
    src="/partners.png"
    alt="Partners logos"
    className="absolute top-0 h-24 object-contain animate-strip2"
    style={{ left: "100%" }} // ещё дальше вправо
  />
</div>

  </div>
</section>














      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl md:text-4xl font-bold mb-6">
            {t("home.cta.title")}
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            {t("home.cta.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-yellow-600 hover:bg-yellow-500 text-black font-semibold px-8 py-4 rounded-full text-lg sm:text-xl transition-all duration-300 hover:scale-105 hover:shadow-xl"
            >
              <Link to="/register">{t("home.cta.apply")}</Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black font-semibold px-8 py-4 rounded-full text-lg sm:text-xl transition-all duration-300 hover:scale-105"
            >
              <Link to="/contact">{t("home.cta.contact")}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
